import React, { useImperativeHandle, useMemo, useState } from 'react';
import { Point, Rate } from '@/types';
import { useSelector } from 'react-redux';
import { selectAllPoints } from '../map/slice/mapSelectors';
import { useDebouncedCallback, useTheme } from '../helpers/hooks';
import { CheckIcon, CloseIcon, TrashIcon } from '@iconicicons/react';
import { IconButton } from '../helpers/IconButton';
import { RatesPatch } from '@/types/api/RateSchedule';
import { PointSelectorModal } from '../points/PointSelectorModal';
import { InsertRow } from '../helpers/InsertRow';
import { isValidNumber } from '../utils/numberUtils';
import { EditablePointList } from '../helpers/EditablePointList';
import { ZoneSelector } from '../helpers/ZoneSelector';

const canRemoveReceiptPoint = (rate: Rate) => {
  return (
    rate.receipt_points?.length > 1 ||
    (rate.delivery_points?.length > 0 && Boolean(rate.receipt_zone))
  );
};

const canRemoveDeliveryPoint = (rate: Rate) => {
  return (
    rate.delivery_points?.length > 1 ||
    (rate.receipt_points?.length > 0 && Boolean(rate.delivery_zone))
  );
};

export type PointRatesRef = {
  getPatch: () => RatesPatch;
};

export type PointRatesProps = {
  style?: React.CSSProperties;
  rateSchedule: string;
  rates: Rate[];
  tspId: string;
  onChange?: () => void;
  onCreate?: (rate: Partial<Rate>) => void;
  onDelete?: (rateId: number) => void;
};

const _PointRates: React.ForwardRefRenderFunction<
  PointRatesRef,
  PointRatesProps
> = (
  { style, rateSchedule, rates, tspId, onChange, onCreate, onDelete },
  ref,
) => {
  const points = useSelector(selectAllPoints);
  const [theme] = useTheme();
  const [updatedRates, setUpdatedRates] = useState<
    RatesPatch['rates']['update']
  >([]);
  const [addPointInfo, setAddPointInfo] = useState<{
    rateId: number | 'new';
    type: 'receipt' | 'delivery';
  } | null>(null);
  const [newRate, setNewRate] = useState<Partial<Rate> | null>(null);

  const isNewRateValid = useMemo(() => {
    if (!newRate) {
      return false;
    }
    if (
      (!newRate.receipt_points || newRate.receipt_points.length === 0) &&
      (!newRate.receipt_zone ||
        !newRate.delivery_points ||
        newRate.delivery_points.length === 0)
    ) {
      return false;
    }
    if (
      (!newRate.delivery_points || newRate.delivery_points.length === 0) &&
      (!newRate.delivery_zone ||
        !newRate.receipt_points ||
        newRate.receipt_points.length === 0)
    ) {
      return false;
    }
    if (!newRate.value || !isValidNumber(newRate.value)) {
      return false;
    }
    return true;
  }, [newRate]);

  const ratesPatch: RatesPatch = useMemo(
    () => ({
      rate_schedule_id: Number(rateSchedule),
      rates: {
        update: updatedRates,
        create: [],
      },
    }),
    [rateSchedule, updatedRates],
  );

  const pointRates = useMemo(() => {
    return rates.filter(
      (rate) =>
        rate.receipt_points?.length > 0 || rate.delivery_points?.length > 0,
    );
  }, [rates]);

  useImperativeHandle(
    ref,
    () => ({
      getPatch() {
        return ratesPatch;
      },
    }),
    [ratesPatch],
  );

  const updateRate = (
    rate: Rate,
    updates: Partial<RatesPatch['rates']['update'][number]>,
  ) => {
    setUpdatedRates((patch) => {
      const newPatch = [...patch];
      const existingUpdateIndex = newPatch.findIndex((r) => r.id === rate.id);
      if (existingUpdateIndex >= 0) {
        newPatch[existingUpdateIndex] = {
          ...newPatch[existingUpdateIndex],
          ...updates,
        };
      } else {
        newPatch.push({
          ...updates,
          id: rate.id,
        });
      }
      return newPatch;
    });
    onChange?.();
  };

  const onChangeValue = useDebouncedCallback(
    (rateId: number, value: number) => {
      const rate = pointRates.find((r) => r.id === rateId);
      if (!rate) {
        return;
      } else {
        updateRate(rate, { value });
      }
    },
    300,
    [pointRates, updateRate],
  );

  const onChangeZone = (
    rateId: number,
    type: 'receipt' | 'delivery',
    zoneId: string,
  ) => {
    const rate = rates.find((r) => r.id === rateId);
    if (!rate) {
      return;
    }
    if (type === 'receipt') {
      updateRate(rate, {
        receipt_zone: isValidNumber(zoneId) ? Number(zoneId) : undefined,
      });
    } else {
      updateRate(rate, {
        delivery_zone: isValidNumber(zoneId) ? Number(zoneId) : undefined,
      });
    }
  };

  const onAddPoint = (
    rateId: number,
    type: 'receipt' | 'delivery',
    pointId: number,
  ) => {
    const rate = rates.find((r) => r.id === rateId);
    if (!rate) {
      return;
    }
    const updatedRate = updatedRates.find((r) => r.id === rate.id) ?? rate;
    if (type === 'receipt') {
      const receiptPoints = updatedRate.receipt_points ?? rate.receipt_points;
      const newReceiptPoints = receiptPoints
        ? [...receiptPoints, pointId]
        : [pointId];
      updateRate(rate, { receipt_points: newReceiptPoints });
    } else {
      const deliveryPoints =
        updatedRate.delivery_points ?? rate.delivery_points;
      const newDeliveryPoints = deliveryPoints
        ? [...deliveryPoints, pointId]
        : [pointId];
      updateRate(rate, { delivery_points: newDeliveryPoints });
    }
  };

  const onRemovePoint = (
    rateId: number,
    type: 'receipt' | 'delivery',
    pointId: number,
  ) => {
    const rate = pointRates.find((r) => r.id === rateId);
    if (!rate) {
      return;
    }
    const updatedRate = updatedRates.find((r) => r.id === rate.id) ?? rate;
    if (type === 'receipt') {
      const receiptPoints = updatedRate.receipt_points ?? rate.receipt_points;
      const newReceiptPoints = receiptPoints.filter((id) => id !== pointId);
      updateRate(rate, { receipt_points: newReceiptPoints });
    } else if (type === 'delivery') {
      const deliveryPoints =
        updatedRate.delivery_points ?? rate.delivery_points;
      const newDeliveryPoints = deliveryPoints.filter((id) => id !== pointId);
      updateRate(rate, { delivery_points: newDeliveryPoints });
    }
  };

  const onAddPointToNewRate = (type: 'receipt' | 'delivery', point: Point) => {
    setNewRate((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [type === 'receipt' ? 'receipt_points' : 'delivery_points']:
          prev.receipt_points?.filter(
            (id) => id.toString() !== point.id.toString(),
          ),
      };
    });
  };

  return (
    <>
      <table
        className="table table-striped table-bordered"
        style={{ width: '100%', ...style }}
      >
        <thead>
          <tr>
            <th>ID</th>
            <th>From</th>
            <th>To</th>
            <th style={{ maxWidth: '200px' }}>Rate</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {newRate !== null ? (
            <tr>
              <th scope="row" className="align-middle">
                <p className="text-center">New</p>
              </th>
              <td align="center">
                Zone
                <ZoneSelector
                  tspId={tspId}
                  value={newRate.receipt_zone?.toString() || ''}
                  className="mb-3"
                  onChange={(evt) => {
                    const zone = isValidNumber(evt.target.value)
                      ? Number(evt.target.value)
                      : undefined;
                    setNewRate((prev) => ({
                      ...prev,
                      receipt_zone: zone,
                    }));
                  }}
                />
                Points
                <EditablePointList
                  points={newRate.receipt_points?.map(
                    (pointId) => points[pointId],
                  )}
                  theme={theme}
                  canAdd
                  canRemove
                  onAddPoint={() =>
                    setAddPointInfo({ rateId: 'new', type: 'receipt' })
                  }
                  onRemovePoint={(point) => {
                    onAddPointToNewRate('receipt', point);
                  }}
                />
              </td>
              <td align="center">
                Zone
                <ZoneSelector
                  tspId={tspId}
                  value={newRate.delivery_zone?.toString() || ''}
                  className="mb-3"
                  onChange={(evt) => {
                    const zone = isValidNumber(evt.target.value)
                      ? Number(evt.target.value)
                      : undefined;
                    setNewRate((prev) => ({
                      ...prev,
                      delivery_zone: zone,
                    }));
                  }}
                />
                Points
                <EditablePointList
                  points={newRate.delivery_points?.map(
                    (pointId) => points[pointId],
                  )}
                  theme={theme}
                  canAdd
                  canRemove
                  onAddPoint={() =>
                    setAddPointInfo({ rateId: 'new', type: 'delivery' })
                  }
                  onRemovePoint={(point) => {
                    onAddPointToNewRate('delivery', point);
                  }}
                />
              </td>
              <td className="align-middle" style={{ maxWidth: '200px' }}>
                <input
                  id={`rate-new`}
                  name={`rate-new`}
                  className="form-control d-flex"
                  type="number"
                  value={newRate.value || ''}
                  onChange={(evt) =>
                    setNewRate((prev) => ({
                      ...prev,
                      value: evt.target.value,
                    }))
                  }
                />
              </td>
              <td className="align-middle">
                <div className="w-100 h-100 d-flex gap-2 justify-content-center align-content-center">
                  <IconButton
                    className="text-success"
                    icon={CheckIcon}
                    disabled={isNewRateValid === false}
                    onClick={() => {
                      onCreate?.(newRate);
                      setNewRate(null);
                    }}
                  />
                  <IconButton
                    className="text-danger"
                    icon={CloseIcon}
                    onClick={() => {
                      setNewRate(null);
                    }}
                  />
                </div>
              </td>
            </tr>
          ) : (
            <tr>
              <td colSpan={5} className="p-0">
                <InsertRow
                  onClick={() =>
                    setNewRate({
                      receipt_points: [],
                      delivery_points: [],
                    })
                  }
                />
              </td>
            </tr>
          )}
          {pointRates.map((rate) => {
            const updatedRate = updatedRates.find((r) => r.id === rate.id);
            const displayRate = updatedRate
              ? { ...rate, ...updatedRate }
              : rate;
            return (
              <tr key={rate.id}>
                <th scope="row" className="align-middle">
                  <p className="text-center">#{rate.id}</p>
                </th>
                <td align="center">
                  Zone
                  <ZoneSelector
                    tspId={tspId}
                    value={displayRate.receipt_zone?.toString() || ''}
                    className="mb-3"
                    onChange={(evt) => {
                      onChangeZone(displayRate.id, 'receipt', evt.target.value);
                    }}
                  />
                  Points
                  <EditablePointList
                    points={displayRate.receipt_points?.map(
                      (pointId) => points[pointId],
                    )}
                    theme={theme}
                    canAdd
                    canRemove={canRemoveReceiptPoint(displayRate)}
                    onAddPoint={() =>
                      setAddPointInfo({ rateId: rate.id, type: 'receipt' })
                    }
                    onRemovePoint={(point) =>
                      onRemovePoint(rate.id, 'receipt', Number(point.id))
                    }
                  />
                </td>
                <td align="center">
                  Zone
                  <ZoneSelector
                    tspId={tspId}
                    value={displayRate.delivery_zone?.toString() || ''}
                    className="mb-3"
                    onChange={(evt) => {
                      onChangeZone(
                        displayRate.id,
                        'delivery',
                        evt.target.value,
                      );
                    }}
                  />
                  Points
                  <EditablePointList
                    points={displayRate.delivery_points?.map(
                      (pointId) => points[pointId],
                    )}
                    theme={theme}
                    canAdd
                    canRemove={canRemoveDeliveryPoint(displayRate)}
                    onAddPoint={() =>
                      setAddPointInfo({ rateId: rate.id, type: 'delivery' })
                    }
                    onRemovePoint={(point) =>
                      onRemovePoint(rate.id, 'delivery', Number(point.id))
                    }
                  />
                </td>
                <td className="align-middle" style={{ maxWidth: '200px' }}>
                  <input
                    id={`rate-${rate.id}`}
                    name={`rate-${rate.id}`}
                    className="form-control d-flex"
                    type="number"
                    defaultValue={Number(rate.value)}
                    onChange={(evt) =>
                      onChangeValue(rate.id, Number(evt.target.value))
                    }
                  />
                </td>
                <td className="align-middle">
                  <div className="w-100 h-100 d-flex justify-content-center align-content-center">
                    <IconButton
                      className="text-danger"
                      icon={TrashIcon}
                      onClick={() => {
                        if (
                          confirm('Are you sure you want to delete this rate?')
                        ) {
                          onDelete?.(rate.id);
                        }
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <PointSelectorModal
        isOpen={addPointInfo !== null}
        onClose={() => setAddPointInfo(null)}
        onSelect={(point) => {
          if (addPointInfo) {
            if (addPointInfo.rateId === 'new') {
              setNewRate((prev) => {
                const updatedReceiptPoints =
                  addPointInfo.type === 'receipt'
                    ? [...(prev?.receipt_points || []), Number(point.id)]
                    : prev?.receipt_points || [];
                const updatedDeliveryPoints =
                  addPointInfo.type === 'delivery'
                    ? [...(prev?.delivery_points || []), Number(point.id)]
                    : prev?.delivery_points || [];
                return {
                  ...prev,
                  receipt_points: updatedReceiptPoints,
                  delivery_points: updatedDeliveryPoints,
                };
              });
            } else {
              onAddPoint(
                addPointInfo.rateId,
                addPointInfo.type,
                Number(point.id),
              );
            }
          }
          setAddPointInfo(null);
        }}
        lockedTsps={[Number(tspId)]}
      />
    </>
  );
};

export const PointRates = React.forwardRef(_PointRates);
